import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Layers,
  Lock,
  ShoppingCart,
  Building2,
  Scale,
  RefreshCw,
  Search,
  CheckCircle2,
  AlertTriangle,
  Package,
  Hammer,
  Wrench,
  Loader2,
  ShieldCheck,
  Sliders,
  ChevronDown,
  ChevronUp,
  FlaskConical
} from 'lucide-react';
import {
  MRPOrderExplosionResponse,
  MRPExplodedMaterialLine,
  MRPExplosionLayer,
  MRPSubcontractingServiceLine,
  SERVICE_EXECUTION_STATES,
  ServiceExecutionState,
  Order360Detail,
  Orders360CatalogResponse,
  CRMProject
} from '../types/crm';
import { OABLineItem } from '../types/oab';

interface MRPExplosionPlannerTabProps {
  token?: string | null;
  projects: CRMProject[];
  initialOrderId?: string;
  initialOrderCodigo?: string;
  onOpenStoreAllocations?: (projectName?: string) => void;
  onOpenBOMAuditForOrder?: (order: { id: string; codigo: string; proyecto: string }) => void;
  onOpenSupplyWithDraftLines?: (lines: OABLineItem[], notes: string) => void;
  onRefreshAllocations?: () => void;
  onNotify?: (type: 'success' | 'error', text: string) => void;
}

export const MRPExplosionPlannerTab: React.FC<MRPExplosionPlannerTabProps> = ({
  token,
  projects,
  initialOrderId,
  initialOrderCodigo,
  onOpenStoreAllocations,
  onOpenBOMAuditForOrder,
  onOpenSupplyWithDraftLines,
  onRefreshAllocations,
  onNotify
}) => {
  const [ordersCatalog, setOrdersCatalog] = useState<Order360Detail[]>([]);
  const [scopeType, setScopeType] = useState<'order' | 'project'>('order');
  const [selectedOrderId, setSelectedOrderId] = useState<string>(initialOrderId || '');
  const [selectedOrderCodigo, setSelectedOrderCodigo] = useState<string>(initialOrderCodigo || '');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [selectorQuery, setSelectorQuery] = useState<string>('');
  const [distribucionDrywallPct, setDistribucionDrywallPct] = useState<number>(0);
  const [includeParametric, setIncludeParametric] = useState<boolean>(true);

  const [explosion, setExplosion] = useState<MRPOrderExplosionResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Filtros de la tabla de explosión
  const [layerFilter, setLayerFilter] = useState<
    'ALL' | MRPExplosionLayer | 'MTO_READY' | 'OAB_DEFICIT' | 'SERVICIOS_OBRA'
  >('ALL');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showFurnitureDrawer, setShowFurnitureDrawer] = useState<boolean>(false);

  // Estado editable de cantidades a reservar (MTO) y a comprar (OAB) por línea
  const [selectedKeys, setSelectedKeys] = useState<Record<string, boolean>>({});
  const [customMtoQty, setCustomMtoQty] = useState<Record<string, number>>({});
  const [customOabQty, setCustomOabQty] = useState<Record<string, number>>({});
  // Selección de Servicios y Suministros Foráneos (PLAN-060 P60-D2 / P60-D3)
  const [selectedServiceIds, setSelectedServiceIds] = useState<Record<string, boolean>>({});

  // Controles de ejecución transaccional (Cero PIN hardcodeado)
  const [supervisorPIN, setSupervisorPIN] = useState<string>('');
  const [persistInNotionRequeridos, setPersistInNotionRequeridos] = useState<boolean>(true);
  const [isExecutingMTO, setIsExecutingMTO] = useState<boolean>(false);
  const [lastMtoResult, setLastMtoResult] = useState<{
    dryRun?: boolean;
    reservedCount: number;
    reservedTotalUSD: number;
    clampedCount?: number;
    syncedNotionRequeridosCount: number;
    message: string;
  } | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const hasValidJwtSession = Boolean(token && token.split('.').length === 3);

  const notify = (type: 'success' | 'error', text: string) => {
    if (onNotify) onNotify(type, text);
  };

  // Cargar lista completa de pedidos con renglones (sin truncar a 220)
  useEffect(() => {
    let mounted = true;
    fetch('/api/orders/catalog')
      .then(r => r.json())
      .then((d: Orders360CatalogResponse) => {
        if (!mounted || !Array.isArray(d.orders)) return;
        const withLines = d.orders.filter(o => (o.linesCount || 0) > 0);
        setOrdersCatalog(withLines);
        if (!selectedOrderId && !selectedOrderCodigo && withLines.length > 0) {
          const preferred =
            withLines.find(o => o.codigo === 'P0386') ||
            withLines.find(o => o.inRampaActivos) ||
            withLines[0];
          if (preferred) {
            setSelectedOrderId(preferred.id);
            setSelectedOrderCodigo(preferred.codigo);
          }
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (initialOrderId) {
      setScopeType('order');
      setSelectedOrderId(initialOrderId);
    }
    if (initialOrderCodigo) {
      setScopeType('order');
      setSelectedOrderCodigo(initialOrderCodigo);
    }
  }, [initialOrderId, initialOrderCodigo]);

  const fetchExplosion = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setErrorMsg(null);
    try {
      const params = new URLSearchParams();
      params.set('scopeType', scopeType);
      if (scopeType === 'project' && selectedProjectId) {
        params.set('projectId', selectedProjectId);
      } else {
        if (selectedOrderId) params.set('orderId', selectedOrderId);
        if (selectedOrderCodigo) params.set('orderCodigo', selectedOrderCodigo);
      }
      if (distribucionDrywallPct > 0) {
        params.set('distribucionDrywallPct', String(distribucionDrywallPct));
      }
      params.set('parametric', includeParametric ? '1' : '0');

      const res = await fetch(`/api/bom/order-explosion?${params.toString()}`, {
        signal: controller.signal
      });
      const data: MRPOrderExplosionResponse = await res.json();
      if (!res.ok || data.error) {
        throw new Error(data.error || `Error HTTP ${res.status}`);
      }
      setExplosion(data);

      // Inicializar selección: las estimaciones paramétricas quedan desmarcadas por defecto para compra OAB
      const initSel: Record<string, boolean> = {};
      const initMto: Record<string, number> = {};
      const initOab: Record<string, number> = {};
      for (const m of data.materials || []) {
        initSel[m.key] =
          !m.esEstimacionParametrica && (m.sugeridoReservarMTO > 0 || m.deficitComprarOAB > 0);
        initMto[m.key] = m.sugeridoReservarMTO;
        initOab[m.key] = m.deficitComprarOAB;
      }
      setSelectedKeys(initSel);
      setCustomMtoQty(initMto);
      setCustomOabQty(initOab);

      const initSrvSel: Record<string, boolean> = {};
      for (const srv of data.serviciosYSuministrosObra || []) {
        initSrvSel[srv.lineId] = srv.estadoServicioObra !== 'EJECUTADO_CONFORME';
      }
      setSelectedServiceIds(initSrvSel);
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      setErrorMsg(err.message || 'No se pudo calcular la explosión MRP en 3 capas.');
    } finally {
      if (abortControllerRef.current === controller) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchExplosion();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [scopeType, selectedOrderId, selectedOrderCodigo, selectedProjectId, distribucionDrywallPct, includeParametric]);

  // Filtro buscable para los 638 pedidos y los 355 proyectos (Cero truncamiento .slice)
  const filteredOrdersCatalog = useMemo(() => {
    const q = selectorQuery.trim().toLowerCase();
    if (!q) return ordersCatalog;
    return ordersCatalog.filter(
      ord =>
        ord.codigo.toLowerCase().includes(q) ||
        ord.clienteNombre.toLowerCase().includes(q) ||
        ord.sucursalNombre.toLowerCase().includes(q) ||
        (ord.proyectoNombre || '').toLowerCase().includes(q) ||
        (selectedOrderId && ord.id === selectedOrderId)
    );
  }, [ordersCatalog, selectorQuery, selectedOrderId]);

  const filteredProjectsCatalog = useMemo(() => {
    const q = selectorQuery.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(
      p =>
        p.nombre.toLowerCase().includes(q) ||
        (p.clienteNombre || '').toLowerCase().includes(q) ||
        (p.sucursalNombre || '').toLowerCase().includes(q) ||
        (selectedProjectId && p.id === selectedProjectId)
    );
  }, [projects, selectorQuery, selectedProjectId]);

  const filteredMaterials = useMemo(() => {
    const list = explosion?.materials || [];
    const q = searchTerm.trim().toLowerCase();
    return list.filter(item => {
      if (layerFilter === 'FABRICACION_BOM' && item.capa !== 'FABRICACION_BOM') return false;
      if (layerFilter === 'EMBALAJE_4UP' && item.capa !== 'EMBALAJE_4UP') return false;
      if (layerFilter === 'FIJACION_OBRA' && item.capa !== 'FIJACION_OBRA') return false;
      if (layerFilter === 'MTO_READY' && (customMtoQty[item.key] ?? item.sugeridoReservarMTO) <= 0) return false;
      if (layerFilter === 'OAB_DEFICIT' && (customOabQty[item.key] ?? item.deficitComprarOAB) <= 0) return false;

      if (!q) return true;
      return (
        item.nombre.toLowerCase().includes(q) ||
        item.sku.toLowerCase().includes(q) ||
        item.matCode.toLowerCase().includes(q) ||
        item.categoria.toLowerCase().includes(q) ||
        item.origenMuebles.some(o => o.toLowerCase().includes(q))
      );
    });
  }, [explosion, layerFilter, searchTerm, customMtoQty, customOabQty]);

  // Totales interactivos según las casillas marcadas y cantidades editadas
  const interactiveSummary = useMemo(() => {
    const list = explosion?.materials || [];
    let selectedMtoLines = 0;
    let selectedMtoUSD = 0;
    let selectedOabLines = 0;
    let selectedOabUSD = 0;
    let selectedOabComercialUSD = 0;

    for (const m of list) {
      if (!selectedKeys[m.key]) continue;
      const mto = Number(customMtoQty[m.key] ?? m.sugeridoReservarMTO ?? 0);
      const oab = Number(customOabQty[m.key] ?? m.deficitComprarOAB ?? 0);
      if (mto > 0) {
        selectedMtoLines++;
        selectedMtoUSD += mto * m.costoUnitarioUSD;
      }
      if (oab > 0) {
        selectedOabLines++;
        selectedOabUSD += oab * m.costoUnitarioUSD;
        selectedOabComercialUSD += Number(m.costoCompraUSD ?? oab * m.costoUnitarioUSD);
      }
    }

    return {
      selectedMtoLines,
      selectedMtoUSD: Number(selectedMtoUSD.toFixed(2)),
      selectedOabLines,
      selectedOabUSD: Number(selectedOabUSD.toFixed(2)),
      selectedOabComercialUSD: Number(selectedOabComercialUSD.toFixed(2))
    };
  }, [explosion, selectedKeys, customMtoQty, customOabQty]);

  // Acción 1: Ejecutar Reserva MTO (o Simulación Dry-Run) hacia Asignación Tiendas + BD_Materiales_Requeridos
  const handleExecuteMTOReservation = async (isDryRun = false) => {
    if (!explosion) return;
    const selectedItems = explosion.materials
      .filter(m => selectedKeys[m.key])
      .map(m => ({
        ...m,
        reserveQtyMTO: Number(customMtoQty[m.key] ?? m.sugeridoReservarMTO ?? 0),
        deficitQtyOAB: Number(customOabQty[m.key] ?? m.deficitComprarOAB ?? 0)
      }))
      .filter(m => m.reserveQtyMTO > 0 || (persistInNotionRequeridos && m.deficitQtyOAB > 0));

    if (selectedItems.length === 0) {
      notify('error', 'Selecciona al menos un insumo con cantidad a reservar o requerimiento activo.');
      return;
    }

    if (!hasValidJwtSession && !supervisorPIN.trim()) {
      notify('error', 'Inicia sesión con tu PIN de operador o ingresa un PIN de Supervisor válido.');
      return;
    }

    setIsExecutingMTO(true);
    setLastMtoResult(null);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (hasValidJwtSession && token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch('/api/bom/order-explosion', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'execute_mto_reservation',
          scopeType,
          orderId: explosion.orderId,
          orderCodigo: explosion.orderCodigo,
          proyectoId: explosion.proyectoId,
          proyectoNombre: explosion.proyectoNombre,
          items: selectedItems,
          persistRequiredMaterials: persistInNotionRequeridos,
          dryRun: isDryRun,
          supervisorPIN: supervisorPIN.trim() || undefined
        })
      });

      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Error ejecutando la operación MRP.');
      }

      setLastMtoResult({
        dryRun: Boolean(data.dryRun),
        reservedCount: data.reservedCount || 0,
        reservedTotalUSD: data.reservedTotalUSD || 0,
        clampedCount: data.clampedCount || 0,
        syncedNotionRequeridosCount: data.syncedNotionRequeridosCount || 0,
        message: data.message || 'Operación MRP ejecutada con éxito.'
      });

      notify('success', data.message || 'Operación MRP procesada correctamente.');
      if (!isDryRun) {
        if (onRefreshAllocations) onRefreshAllocations();
        await fetchExplosion();
      }
    } catch (err: any) {
      notify('error', err.message || 'Error al ejecutar la reserva MTO.');
    } finally {
      setIsExecutingMTO(false);
    }
  };

  // Acción 2: Generar Requisición OAB por Faltantes Netos a 1 Clic con Conversión UoM de 3 Niveles
  const handleGenerateOABRequisition = () => {
    if (!explosion) return;
    const deficitItems = explosion.materials.filter(
      m => selectedKeys[m.key] && Number(customOabQty[m.key] ?? m.deficitComprarOAB ?? 0) > 0
    );

    if (deficitItems.length === 0) {
      notify('error', 'No hay insumos seleccionados con Déficit Neto (> 0) para comprar en OAB.');
      return;
    }

    const oabDraftLines: OABLineItem[] = deficitItems.map(m => {
      const netDeficitStockQty = Number(customOabQty[m.key] ?? m.deficitComprarOAB ?? 1);
      const commercialBuyQty = Number(m.cantidadComercialCompraOAB ?? Math.ceil(netDeficitStockQty));
      const unitStockCost = Number(m.costoUnitarioUSD) > 0 ? Number(m.costoUnitarioUSD) : 1;
      const commercialSubtotalUSD = Number(m.costoCompraUSD ?? Number((commercialBuyQty * unitStockCost).toFixed(2)));
      const commercialUnitCost =
        commercialBuyQty > 0 ? Number((commercialSubtotalUSD / commercialBuyQty).toFixed(2)) : unitStockCost;

      return {
        nombre: `[${m.sku || m.matCode}] ${m.nombre}`,
        insumoId: m.insumoNotionId || m.sku,
        dashboardId: m.insumoNotionId || m.sku,
        cantidadStock: m.stockLibre,
        stockMinimo: 0,
        deficit: netDeficitStockQty,
        cantidadSugerida: commercialBuyQty,
        cantidadSolicitada: commercialBuyQty,
        cantidadAprobada: commercialBuyQty,
        costoUnitarioUSD: commercialUnitCost,
        subtotalUSD: commercialSubtotalUSD,
        prioridad: m.capa === 'FABRICACION_BOM' ? 'Alta' : 'Media',
        pedidoId: explosion.orderId,
        proyectoId: explosion.proyectoId || undefined,
        proyectoNombre: explosion.proyectoNombre,
        unidadMedida: m.unidad,
        uomCompra: m.uomCompra || m.unidad,
        factorUoM: m.factorUoM || 1,
        cantidadCompra: commercialBuyQty,
        costoCompraUSD: commercialUnitCost
      };
    });

    const notes = `[EXPLOSIÓN MRP 3 CAPAS · PLAN-053-R] Pedido: ${explosion.orderCodigo} | Obra: ${explosion.proyectoNombre} | Cliente: ${explosion.clienteNombre} (${explosion.sucursalNombre}) | Sustrato: ${explosion.sustratoPared} / ${explosion.sustratoPiso}. Faltante neto de ${oabDraftLines.length} insumos (Desembolso Comercial OAB: $${interactiveSummary.selectedOabComercialUSD.toFixed(2)} USD).`;

    if (onOpenSupplyWithDraftLines) {
      onOpenSupplyWithDraftLines(oabDraftLines, notes);
    } else {
      notify('success', `Pre-cargados ${oabDraftLines.length} insumos con conversión comercial UoM para OAB.`);
    }
  };

  // PLAN-060 (P60-D2): Actualizar en vivo el Estado Servicio Obra en Notion y Edge KV sin tocar el stock físico
  const handleUpdateServiceLineStatus = async (
    srvLine: MRPSubcontractingServiceLine,
    nextStatus: ServiceExecutionState
  ) => {
    setExplosion(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        serviciosYSuministrosObra: (prev.serviciosYSuministrosObra || []).map(s =>
          s.lineId === srvLine.lineId ? { ...s, estadoServicioObra: nextStatus } : s
        )
      };
    });

    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (hasValidJwtSession && token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch('/api/orders/upsert', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'update_line_status',
          orderId: srvLine.orderId,
          lineId: srvLine.lineId,
          estadoServicioObra: nextStatus,
          supervisorPIN: supervisorPIN.trim() || undefined
        })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'No se pudo sincronizar el estado del servicio.');
      }
      notify('success', `Estado de ${srvLine.codeEtiqueta} actualizado a ${nextStatus}.`);
    } catch (err: any) {
      notify('error', err.message || 'Error al actualizar estado del servicio en obra.');
    }
  };

  // PLAN-060 (P60-D3): Pre-Cargar en Solicitud OAB únicamente los Servicios / Suministros Foráneos seleccionados
  const handleGenerateServiceSubcontractingOAB = () => {
    if (!explosion) return;
    const srvList = (explosion.serviciosYSuministrosObra || []).filter(
      s => selectedServiceIds[s.lineId]
    );
    if (srvList.length === 0) {
      notify('error', 'Selecciona al menos un Servicio en Obra o Suministro de Envío Directo en la bandeja.');
      return;
    }

    const oabDraftLines: OABLineItem[] = srvList.map(s => {
      const qty = Number(s.cantidad) > 0 ? Number(s.cantidad) : 1;
      const unitCost = Number(s.precioUnitarioUSD) > 0 ? Number(s.precioUnitarioUSD) : 1;
      const subUSD = Number((qty * unitCost).toFixed(2));
      const routeTag = s.tipoLinea === 'SERVICIO_INTANGIBLE' ? 'SERVICIO_SITIO' : 'DIRECTO_OBRA';
      return {
        nombre: `[${s.codeEtiqueta}] [RUTA: ${routeTag}] ${s.descEtiqueta || s.desc}`,
        insumoId: s.code,
        dashboardId: s.code,
        cantidadStock: 0,
        stockMinimo: 0,
        deficit: qty,
        cantidadSugerida: qty,
        cantidadSolicitada: qty,
        cantidadAprobada: qty,
        costoUnitarioUSD: unitCost,
        subtotalUSD: subUSD,
        prioridad: 'Alta',
        pedidoId: s.orderId || explosion.orderId,
        proyectoId: explosion.proyectoId || undefined,
        proyectoNombre: explosion.proyectoNombre,
        rutaLogistica: routeTag,
        orderLineId: s.lineId,
        isDirectOrService: true,
        unidadMedida: s.unidadMedida || 'SERV',
        uomCompra: s.unidadMedida || 'SERV',
        factorUoM: 1,
        cantidadCompra: qty,
        costoCompraUSD: unitCost
      };
    });

    const totalSrvUSD = oabDraftLines.reduce((acc, l) => acc + (l.subtotalUSD || 0), 0);
    const notes = `[RUTA: DIRECTO_OBRA / SERVICIO_SITIO · PLAN-060] Subcontratación de Servicios en Obra y Suministros con Envío Directo (0 paso por galpón) | Pedido: ${explosion.orderCodigo} | Obra: ${explosion.proyectoNombre} | Cliente: ${explosion.clienteNombre} (${explosion.sucursalNombre}) | Total: $${totalSrvUSD.toFixed(2)} USD (${oabDraftLines.length} renglones).`;

    if (onOpenSupplyWithDraftLines) {
      onOpenSupplyWithDraftLines(oabDraftLines, notes);
    } else {
      notify(
        'success',
        `Pre-cargados ${oabDraftLines.length} servicios/suministros con etiqueta [RUTA: DIRECTO_OBRA / SERVICIO_SITIO] en OAB.`
      );
    }
  };

  const renderLayerBadge = (capa: MRPExplosionLayer, label: string) => {
    if (capa === 'FABRICACION_BOM') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-indigo-950/80 text-indigo-300 border border-indigo-700/60">
          <Hammer className="w-3 h-3" /> {label}
        </span>
      );
    }
    if (capa === 'EMBALAJE_4UP') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-950/80 text-amber-300 border border-amber-700/60">
          <Package className="w-3 h-3" /> {label}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-teal-950/80 text-teal-300 border border-teal-700/60">
        <Wrench className="w-3 h-3" /> {label}
      </span>
    );
  };

  const renderCoverageBadge = (line: MRPExplodedMaterialLine) => {
    if (line.estadoCobertura === 'YA_RESERVADO') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-950/80 text-purple-300 border border-purple-700/60">
          🟣 Reservado Obra
        </span>
      );
    }
    if (line.estadoCobertura === 'CUBIERTO_STOCK') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-950/80 text-emerald-300 border border-emerald-700/60">
          🟢 Cubierto Stock
        </span>
      );
    }
    if (line.estadoCobertura === 'PARCIAL') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-950/80 text-amber-300 border border-amber-700/60">
          🟡 Parcial MTO+OAB
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-950/80 text-rose-300 border border-rose-700/60">
        🔴 Déficit OAB
      </span>
    );
  };

  return (
    <div className="space-y-4 text-slate-100">
      {/* BANNER CONTEXTUAL DE ALCANCE Y SUSTRATO FÍSICO DE SUCURSAL */}
      <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-4 shadow-lg">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-extrabold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                FASE 4 · PLAN-053-R (Odoo 18 mrp.production · UoM 3 Niveles)
              </span>
              <h3 className="text-base font-extrabold text-white flex items-center gap-2">
                <Layers className="w-4 h-4 text-emerald-400" />
                Planificador de Explosión MRP Ex-Ante en 3 Capas + Reserva MTO y OAB a 1 Clic
              </h3>
            </div>
            <p className="text-xs text-slate-400">
              Cruza automáticamente <strong className="text-slate-200">Capa 1 (Fabricación BOM Pura + Prorrateo 4-UP)</strong>,{' '}
              <strong className="text-slate-200">Capa 2 (Embalaje 4-UP Deduplicado)</strong> y{' '}
              <strong className="text-slate-200">Capa 3 (Kit Fijación según Sustrato Pared/Piso de Sucursal)</strong>{' '}
              con conversión comercial UoM (<strong className="text-emerald-300">Consumo BOM → Stock Base → Compra OAB</strong>).
            </p>
          </div>

          {/* Selector de Modo: Por Pedido vs Por Proyecto */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-lg bg-slate-950 p-1 border border-slate-800">
              <button
                type="button"
                onClick={() => setScopeType('order')}
                className={`px-3 py-1.5 rounded-md text-xs font-bold transition ${
                  scopeType === 'order'
                    ? 'bg-emerald-600 text-white shadow'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                📦 Por Pedido Individual ({ordersCatalog.length})
              </button>
              <button
                type="button"
                onClick={() => {
                  setScopeType('project');
                  if (!selectedProjectId && explosion?.proyectoId) {
                    setSelectedProjectId(explosion.proyectoId);
                  }
                }}
                className={`px-3 py-1.5 rounded-md text-xs font-bold transition ${
                  scopeType === 'project'
                    ? 'bg-emerald-600 text-white shadow'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                🏢 Consolidado por Obra ({projects.length})
              </button>
            </div>

            <button
              type="button"
              onClick={fetchExplosion}
              disabled={loading}
              className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold border border-slate-700 flex items-center gap-1.5"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-emerald-400' : ''}`} />
              Recalcular MRP
            </button>
          </div>
        </div>

        {/* Fila 2: Selector Buscable de Pedido/Obra + Sustrato de Pared/Piso + Simulador Drywall */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mt-4 pt-3 border-t border-slate-800/80 items-end">
          <div className="md:col-span-5 space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="block text-[11px] font-bold text-slate-400 uppercase">
                {scopeType === 'order'
                  ? `Pedido con Renglones 4-UP (${filteredOrdersCatalog.length}/${ordersCatalog.length})`
                  : `Proyecto de Tienda (${filteredProjectsCatalog.length}/${projects.length})`}
              </label>
              <input
                type="text"
                value={selectorQuery}
                onChange={e => setSelectorQuery(e.target.value)}
                placeholder="Filtrar pedido, cliente o sede..."
                className="bg-slate-950 border border-slate-700 rounded px-2 py-0.5 text-[11px] text-slate-200 w-44 focus:outline-none focus:border-emerald-500"
              />
            </div>
            {scopeType === 'order' ? (
              <select
                value={selectedOrderId}
                onChange={e => {
                  const id = e.target.value;
                  setSelectedOrderId(id);
                  const found = ordersCatalog.find(o => o.id === id);
                  if (found) setSelectedOrderCodigo(found.codigo);
                }}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white font-semibold focus:outline-none focus:border-emerald-500"
              >
                {filteredOrdersCatalog.map(ord => (
                  <option key={ord.id} value={ord.id}>
                    {ord.inRampaActivos ? '🚚 [RAMPA] ' : '📦 '}
                    {ord.codigo} — {ord.clienteNombre} ({ord.sucursalNombre}) · {ord.linesCount} líneas /{' '}
                    {ord.unidadesTotales} unds
                  </option>
                ))}
              </select>
            ) : (
              <select
                value={selectedProjectId}
                onChange={e => setSelectedProjectId(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white font-semibold focus:outline-none focus:border-emerald-500"
              >
                <option value="">-- Seleccionar Proyecto de Tienda --</option>
                {filteredProjectsCatalog.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.nombre} — {p.clienteNombre} ({p.sucursalNombre})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Contexto de Obra y Sustratos Físicos */}
          <div className="md:col-span-4 bg-slate-950/90 border border-slate-800 rounded-lg px-3 py-2 flex flex-col justify-between">
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-slate-400 font-semibold">Cliente / Sede:</span>
              <span className="text-emerald-300 font-bold truncate max-w-[230px]">
                {explosion?.clienteNombre || '—'} · {explosion?.sucursalNombre || '—'}
              </span>
            </div>
            <div className="flex items-center justify-between text-[11px] mt-1">
              <span className="text-slate-400 font-semibold">Sustrato Pared / Piso:</span>
              <span className="text-amber-300 font-bold">
                🧱 {explosion?.sustratoPared || 'Mampostería'} | ⬜ {explosion?.sustratoPiso || 'Porcelanato'}
              </span>
            </div>
          </div>

          {/* Ajuste de % Drywall y Fallback Paramétrico */}
          <div className="md:col-span-3 bg-slate-950/90 border border-slate-800 rounded-lg px-3 py-2 space-y-1.5">
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-slate-400 font-semibold flex items-center gap-1">
                <Sliders className="w-3 h-3 text-teal-400" /> % Pared Drywall (Capa 3):
              </span>
              <select
                value={distribucionDrywallPct}
                onChange={e => setDistribucionDrywallPct(Number(e.target.value))}
                className="bg-slate-900 border border-slate-700 rounded px-1.5 py-0.5 text-[11px] font-bold text-teal-300"
              >
                <option value={0}>Auto Sustrato ({explosion?.distribucionDrywallPct || 0}%)</option>
                <option value={25}>25% Drywall / 75% Mampost.</option>
                <option value={50}>50% Mixto Drywall</option>
                <option value={75}>75% Drywall / 25% Mampost.</option>
                <option value={100}>100% Tabiquería Drywall</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-[11px] text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={includeParametric}
                onChange={e => setIncludeParametric(e.target.checked)}
                className="rounded border-slate-700 bg-slate-900 text-emerald-500"
              />
              <span>Incluir est. paramétrica p/ muebles a medida</span>
            </label>
          </div>
        </div>
      </div>

      {/* BANDA DE 4 KPIS INDUSTRIALES DE LA EXPLOSIÓN */}
      {explosion && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-3.5">
            <div className="flex items-center justify-between text-xs text-slate-400 font-semibold">
              <span>DEMANDA FÍSICA DEL PEDIDO</span>
              <Package className="w-4 h-4 text-sky-400" />
            </div>
            <div className="mt-1.5 flex items-baseline gap-2">
              <span className="text-2xl font-black text-white">{explosion.totalUnitsPT}</span>
              <span className="text-xs text-slate-400 font-semibold">
                muebles ({explosion.totalLinesPT} renglones)
              </span>
            </div>
            <div className="mt-1 text-[11px] text-slate-400 flex items-center justify-between">
              <span>📦 {explosion.totalBultos4UP} Bultos 4-UP</span>
              <span>
                🔩 {explosion.totalPuntosPared} pared / {explosion.totalPuntosPiso} piso
              </span>
            </div>
          </div>

          <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-3.5">
            <div className="flex items-center justify-between text-xs text-slate-400 font-semibold">
              <span>DESGLOSE EN 3 CAPAS MRP</span>
              <Layers className="w-4 h-4 text-indigo-400" />
            </div>
            <div className="mt-1.5 flex items-baseline gap-2">
              <span className="text-2xl font-black text-indigo-300">
                {explosion.kpis.totalInsumosUnicos}
              </span>
              <span className="text-xs text-slate-400 font-semibold">insumos industriales únicos</span>
            </div>
            <div className="mt-1 text-[11px] text-slate-300 flex items-center gap-2">
              <span className="text-indigo-300 font-bold">C1 (BOM): {explosion.kpis.capa1Count}</span>
              <span>·</span>
              <span className="text-amber-300 font-bold">C2 (Embalaje): {explosion.kpis.capa2Count}</span>
              <span>·</span>
              <span className="text-teal-300 font-bold">C3 (Obra): {explosion.kpis.capa3Count}</span>
            </div>
          </div>

          <div className="bg-slate-900/90 border border-emerald-900/50 rounded-xl p-3.5">
            <div className="flex items-center justify-between text-xs text-emerald-300 font-semibold">
              <span>DISPONIBLE PARA RESERVAR (MTO)</span>
              <Lock className="w-4 h-4 text-emerald-400" />
            </div>
            <div className="mt-1.5 flex items-baseline gap-2">
              <span className="text-2xl font-black text-emerald-400">
                ${interactiveSummary.selectedMtoUSD.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
              <span className="text-xs text-slate-400 font-semibold">
                ({interactiveSummary.selectedMtoLines} líneas c/ stock)
              </span>
            </div>
            <div className="mt-1 text-[11px] text-slate-400 flex items-center justify-between">
              <span>🟢 {explosion.kpis.cubiertosStockCount} cubiertos hoy</span>
              <span>🟣 {explosion.kpis.yaReservadosCount} ya apartados</span>
            </div>
          </div>

          <div className="bg-slate-900/90 border border-rose-900/50 rounded-xl p-3.5">
            <div className="flex items-center justify-between text-xs text-rose-300 font-semibold">
              <span>COMPRA COMERCIAL OAB (3 NIVELES UOM)</span>
              <ShoppingCart className="w-4 h-4 text-rose-400" />
            </div>
            <div className="mt-1.5 flex items-baseline gap-2">
              <span className="text-2xl font-black text-rose-400">
                ${interactiveSummary.selectedOabComercialUSD.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
              <span className="text-xs text-slate-400 font-semibold">
                ({interactiveSummary.selectedOabLines} faltantes · Neto ${interactiveSummary.selectedOabUSD.toFixed(2)})
              </span>
            </div>
            <div className="mt-1 text-[11px] text-slate-400 flex items-center justify-between">
              <span>Costo Teórico Total:</span>
              <span className="font-bold text-slate-200">
                ${explosion.kpis.costoTotalTeoricoUSD.toLocaleString('en-US', { minimumFractionDigits: 2 })} USD
              </span>
            </div>
          </div>
        </div>
      )}

      {/* BARRA DE ACCIÓN A 1 CLIC: SIMULACIÓN DRY-RUN, RESERVA MTO Y REQUISICIÓN OAB */}
      {explosion && (
        <div className="bg-gradient-to-r from-slate-900 via-slate-900 to-emerald-950/40 border border-emerald-800/50 rounded-xl p-3.5 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            {hasValidJwtSession ? (
              <div className="flex items-center gap-2 bg-emerald-950/60 border border-emerald-700/60 rounded-lg px-2.5 py-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                <span className="text-[11px] font-bold text-emerald-300">
                  🔐 Sesión Activa Autorizada (JWT HS256)
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2 bg-slate-950 border border-slate-800 rounded-lg px-2.5 py-1.5">
                <ShieldCheck className="w-4 h-4 text-amber-400" />
                <span className="text-[11px] font-bold text-slate-300">PIN Supervisor:</span>
                <input
                  type="password"
                  value={supervisorPIN}
                  onChange={e => setSupervisorPIN(e.target.value)}
                  placeholder="PIN"
                  className="w-16 bg-slate-900 border border-slate-700 rounded px-2 py-0.5 text-xs font-mono text-emerald-300 focus:outline-none"
                />
              </div>
            )}

            <label className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={persistInNotionRequeridos}
                onChange={e => setPersistInNotionRequeridos(e.target.checked)}
                className="rounded border-slate-700 bg-slate-900 text-emerald-500"
              />
              <span>Upsert Idempotente en BD_Materiales_Requeridos</span>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Botón 0: Simular Reserva (Dry-Run sin mutar KV ni Notion) */}
            <button
              type="button"
              onClick={() => handleExecuteMTOReservation(true)}
              disabled={isExecutingMTO || interactiveSummary.selectedMtoLines === 0}
              className="px-3 py-2 rounded-lg bg-cyan-950/80 hover:bg-cyan-900 text-cyan-200 border border-cyan-700/60 disabled:opacity-50 text-xs font-bold flex items-center gap-1.5 transition"
              title="Valida permisos y calcula el clamping de stock libre real en el servidor sin modificar KV ni Notion"
            >
              <FlaskConical className="w-3.5 h-3.5 text-cyan-400" />
              <span>🧪 Simular Reserva (Dry-Run)</span>
            </button>

            {/* Botón 1: Reserva MTO a 1 Clic hacia Asignación Tiendas */}
            <button
              type="button"
              onClick={() => handleExecuteMTOReservation(false)}
              disabled={isExecutingMTO || interactiveSummary.selectedMtoLines === 0}
              className="px-3.5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-extrabold shadow-lg flex items-center gap-2 transition"
            >
              {isExecutingMTO ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Lock className="w-4 h-4" />
              )}
              <span>
                🔒 Reservar Stock en Asignación Tiendas ({interactiveSummary.selectedMtoLines} insumos · $
                {interactiveSummary.selectedMtoUSD.toFixed(2)})
              </span>
            </button>

            {/* Botón 2: Generar Requisición OAB por Faltantes Netos a 1 Clic */}
            <button
              type="button"
              onClick={handleGenerateOABRequisition}
              disabled={interactiveSummary.selectedOabLines === 0}
              className="px-3.5 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-xs font-extrabold shadow-lg flex items-center gap-2 transition"
            >
              <ShoppingCart className="w-4 h-4" />
              <span>
                🛒 Generar Requisición OAB ({interactiveSummary.selectedOabLines} insumos · $
                {interactiveSummary.selectedOabComercialUSD.toFixed(2)})
              </span>
            </button>

            {/* Puentes directos a Asignación Tiendas y Auditoría BOM */}
            {onOpenStoreAllocations && (
              <button
                type="button"
                onClick={() => onOpenStoreAllocations(explosion.proyectoNombre)}
                className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-indigo-300 border border-indigo-700/50 text-xs font-bold flex items-center gap-1.5"
              >
                <Building2 className="w-3.5 h-3.5" />
                Ver en Asignación Tiendas
              </button>
            )}

            {onOpenBOMAuditForOrder && (
              <button
                type="button"
                onClick={() =>
                  onOpenBOMAuditForOrder({
                    id: explosion.orderId,
                    codigo: explosion.orderCodigo,
                    proyecto: explosion.proyectoNombre
                  })
                }
                className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-teal-300 border border-teal-700/50 text-xs font-bold flex items-center gap-1.5"
              >
                <Scale className="w-3.5 h-3.5" />
                Auditoría BOM Ex-Post
              </button>
            )}
          </div>
        </div>
      )}

      {/* BANNER DE CONFIRMACIÓN TRANSACCIONAL MTO O DRY-RUN */}
      {lastMtoResult && (
        <div
          className={`rounded-xl p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border ${
            lastMtoResult.dryRun
              ? 'bg-cyan-950/70 border-cyan-600/60 text-cyan-200'
              : 'bg-emerald-950/70 border-emerald-600/60 text-emerald-200'
          }`}
        >
          <div className="flex items-center gap-2 text-xs font-semibold">
            <CheckCircle2
              className={`w-4 h-4 shrink-0 ${
                lastMtoResult.dryRun ? 'text-cyan-400' : 'text-emerald-400'
              }`}
            />
            <span>{lastMtoResult.message}</span>
          </div>
          {!lastMtoResult.dryRun && onOpenStoreAllocations && explosion && (
            <button
              type="button"
              onClick={() => onOpenStoreAllocations(explosion.proyectoNombre)}
              className="px-3 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold shrink-0"
            >
              Abrir Asignación Tiendas ({explosion.proyectoNombre}) →
            </button>
          )}
        </div>
      )}

      {/* FILTROS POR CAPA MRP Y BUSCADOR DE INSUMOS */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-slate-900/70 border border-slate-800 rounded-xl p-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {[
            { id: 'ALL', label: `Todas las Capas (${explosion?.materials.length || 0})` },
            { id: 'FABRICACION_BOM', label: `⚙️ Capa 1 · Fabricación BOM (${explosion?.kpis.capa1Count || 0})` },
            { id: 'EMBALAJE_4UP', label: `📦 Capa 2 · Embalaje 4-UP (${explosion?.kpis.capa2Count || 0})` },
            { id: 'FIJACION_OBRA', label: `🔩 Capa 3 · Fijación Sustrato (${explosion?.kpis.capa3Count || 0})` },
            {
              id: 'SERVICIOS_OBRA',
              label: `⚡ Servicios y Suministros de Obra (${explosion?.serviciosYSuministrosObra?.length || 0})`
            },
            { id: 'MTO_READY', label: '🟢 Con Stock p/ Reservar MTO' },
            { id: 'OAB_DEFICIT', label: '🔴 Con Déficit p/ Comprar OAB' }
          ].map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setLayerFilter(tab.id as any)}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition ${
                layerFilter === tab.id
                  ? 'bg-emerald-600 text-white shadow'
                  : 'bg-slate-950 text-slate-400 hover:text-slate-200 border border-slate-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Buscar insumo, SKU o mueble..."
              className="pl-8 pr-3 py-1.5 bg-slate-950 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-emerald-500 w-60"
            />
          </div>

          <button
            type="button"
            onClick={() => setShowFurnitureDrawer(v => !v)}
            className="px-2.5 py-1.5 rounded-lg bg-slate-950 hover:bg-slate-800 border border-slate-700 text-xs font-bold text-slate-300 flex items-center gap-1"
          >
            <span>Muebles del Pedido ({explosion?.totalLinesPT || 0})</span>
            {showFurnitureDrawer ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* PANEL COLAPSABLE DE MUEBLES DEL PEDIDO (CON RECETA EXPLICITA VS PARAMÉTRICA) */}
      {showFurnitureDrawer && explosion && (
        <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-3.5 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <h4 className="text-xs font-extrabold text-emerald-400 uppercase mb-2">
              ✅ Muebles con Receta BOM Explícita ({explosion.mueblesConReceta.length})
            </h4>
            <div className="max-h-40 overflow-y-auto space-y-1 pr-1">
              {explosion.mueblesConReceta.map(m => (
                <div
                  key={m.lineId}
                  className="flex items-center justify-between text-xs bg-slate-950/80 border border-slate-800 rounded px-2.5 py-1.5"
                >
                  <div className="truncate pr-2">
                    <span className="font-mono font-bold text-emerald-300">{m.codeEtiqueta}</span>{' '}
                    <span className="text-slate-300">{m.desc}</span>
                  </div>
                  <div className="shrink-0 text-[11px] text-slate-400 font-mono">
                    x{m.cantidad} · {m.insumosCount} ins. · ${m.costoTotalBOMUSD.toFixed(2)}
                  </div>
                </div>
              ))}
              {explosion.mueblesConReceta.length === 0 && (
                <p className="text-xs text-slate-500 italic">
                  Este pedido contiene únicamente muebles especiales a medida (cubiertos por estimación paramétrica).
                </p>
              )}
            </div>
          </div>

          <div>
            <h4 className="text-xs font-extrabold text-amber-400 uppercase mb-2">
              📐 Muebles Especiales a Medida con Estimación Paramétrica ({explosion.mueblesSinReceta.length})
            </h4>
            <div className="max-h-40 overflow-y-auto space-y-1 pr-1">
              {explosion.mueblesSinReceta.map(m => (
                <div
                  key={m.lineId}
                  className="flex items-center justify-between text-xs bg-slate-950/80 border border-slate-800 rounded px-2.5 py-1.5"
                >
                  <div className="truncate pr-2">
                    <span className="font-mono font-bold text-amber-300">{m.codeEtiqueta}</span>{' '}
                    <span className="text-slate-300">{m.desc}</span>
                  </div>
                  <div className="shrink-0 text-[11px] text-amber-300 font-semibold">
                    x{m.cantidad} · {m.familia}
                  </div>
                </div>
              ))}
              {explosion.mueblesSinReceta.length === 0 && (
                <p className="text-xs text-slate-500 italic">
                  100% de los renglones tienen receta BOM explícita en el catálogo maestro.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* BANDEJA INTERACTIVA DE SERVICIOS Y SUMINISTROS DE OBRA (PLAN-060 P60-D2 y P60-D3) */}
      {explosion &&
        (layerFilter === 'SERVICIOS_OBRA' ||
          ((explosion.serviciosYSuministrosObra?.length || 0) > 0 && layerFilter === 'ALL')) && (
          <div className="bg-fuchsia-950/20 border border-fuchsia-500/40 rounded-xl p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="text-xs font-extrabold text-fuchsia-300 uppercase flex items-center gap-2">
                  <span>
                    ⚡ Bandeja de Servicios en Obra y Suministros de Envío Directo (
                    {explosion.serviciosYSuministrosObra?.length || 0} renglones · Cortafuegos MTO Activo)
                  </span>
                </h4>
                <p className="text-[11px] text-slate-400">
                  Excluidos de Capas 1–3 de galpón para no distorsionar stock físico ni etiquetas 4-UP. Actualiza su
                  ejecución en vivo o pre-cárgalos en una Solicitud OAB de Subcontratación / Envío Directo.
                </p>
              </div>

              <button
                type="button"
                onClick={handleGenerateServiceSubcontractingOAB}
                disabled={(explosion.serviciosYSuministrosObra?.length || 0) === 0}
                className="px-3.5 py-2 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-500 disabled:opacity-40 text-white text-xs font-extrabold shadow-lg flex items-center gap-2 transition"
              >
                <ShoppingCart className="w-3.5 h-3.5" />
                <span>🛒 Solicitar OAB Subcontratación / Envío Directo</span>
              </button>
            </div>

            {(explosion.serviciosYSuministrosObra?.length || 0) === 0 ? (
              <div className="py-6 text-center text-xs text-slate-500 italic bg-slate-950/60 rounded-lg border border-slate-800">
                Este pedido/proyecto no contiene renglones SERVICIO_INTANGIBLE (SERV-*) ni SUMINISTRO_FORANEO (DIRECTO_OBRA).
              </div>
            ) : (
              <div className="overflow-x-auto border border-fuchsia-500/30 rounded-xl">
                <table className="w-full text-left border-collapse text-xs">
                  <thead className="bg-slate-950 text-[10px] uppercase text-slate-400 border-b border-slate-800">
                    <tr>
                      <th className="py-2 px-3 w-8">Sel.</th>
                      <th className="py-2 px-3">Código / Renglón</th>
                      <th className="py-2 px-3">Clasificación & Ruta Logística</th>
                      <th className="py-2 px-3 text-right">Cant./Med.</th>
                      <th className="py-2 px-3 text-right">Precio Unit.</th>
                      <th className="py-2 px-3 text-right">Subtotal USD</th>
                      <th className="py-2 px-3">Estado Servicio / Ejecución Obra</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/70 bg-slate-900/80">
                    {(explosion.serviciosYSuministrosObra || []).map(srv => (
                      <tr key={srv.lineId} className="hover:bg-slate-800/60">
                        <td className="py-2 px-3">
                          <input
                            type="checkbox"
                            checked={Boolean(selectedServiceIds[srv.lineId])}
                            onChange={e =>
                              setSelectedServiceIds(prev => ({
                                ...prev,
                                [srv.lineId]: e.target.checked
                              }))
                            }
                            className="rounded border-slate-700 bg-slate-950 text-fuchsia-500"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <div className="font-mono font-bold text-fuchsia-300">{srv.codeEtiqueta}</div>
                          <div className="text-[11px] text-slate-200">{srv.descEtiqueta}</div>
                        </td>
                        <td className="py-2 px-3">
                          <span className="px-2 py-0.5 rounded bg-fuchsia-950/90 text-fuchsia-200 border border-fuchsia-700/50 font-mono text-[10px] font-bold">
                            {srv.tipoLinea === 'SERVICIO_INTANGIBLE'
                              ? '⚡ SERVICIO SITIO'
                              : '🚚 SUMINISTRO FORÁNEO'}
                          </span>
                          <span className="ml-1.5 text-[10px] font-mono text-slate-400">
                            [{srv.rutaLogistica}] · 0 Bultos QR
                          </span>
                        </td>
                        <td className="py-2 px-3 text-right font-mono font-bold text-white">
                          {srv.cantidad} <span className="text-[10px] text-cyan-300">{srv.unidadMedida}</span>
                        </td>
                        <td className="py-2 px-3 text-right font-mono text-slate-300">
                          ${srv.precioUnitarioUSD.toFixed(2)}
                        </td>
                        <td className="py-2 px-3 text-right font-mono font-bold text-emerald-300">
                          ${srv.subtotalPrecioUSD.toFixed(2)}
                        </td>
                        <td className="py-2 px-3">
                          <select
                            value={srv.estadoServicioObra || 'PENDIENTE'}
                            onChange={e =>
                              handleUpdateServiceLineStatus(
                                srv,
                                e.target.value as ServiceExecutionState
                              )
                            }
                            className="px-2 py-1 rounded bg-slate-950 border border-fuchsia-500/40 text-fuchsia-200 text-[11px] font-mono font-bold"
                          >
                            {SERVICE_EXECUTION_STATES.map(st => (
                              <option key={st} value={st}>
                                {st}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      {/* TABLA MAESTRA DE EXPLOSIÓN MRP EN 3 CAPAS CON UOM DE 3 NIVELES */}
      {errorMsg && (
        <div className="bg-rose-950/60 border border-rose-700 rounded-xl p-4 text-xs text-rose-200 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      <div className="bg-slate-900/90 border border-slate-800 rounded-xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto max-h-[500px]">
          <table className="w-full text-left border-collapse">
            <thead className="bg-slate-950 text-[11px] font-bold text-slate-400 uppercase sticky top-0 z-10 border-b border-slate-800">
              <tr>
                <th className="py-2.5 px-3 w-8">
                  <input
                    type="checkbox"
                    checked={
                      filteredMaterials.length > 0 &&
                      filteredMaterials.every(m => Boolean(selectedKeys[m.key]))
                    }
                    onChange={e => {
                      const checked = e.target.checked;
                      const next = { ...selectedKeys };
                      for (const m of filteredMaterials) next[m.key] = checked;
                      setSelectedKeys(next);
                    }}
                    className="rounded border-slate-700 bg-slate-900 text-emerald-500"
                  />
                </th>
                <th className="py-2.5 px-3">Capa MRP</th>
                <th className="py-2.5 px-3">SKU / Insumo Industrial & Origen PT</th>
                <th className="py-2.5 px-3 text-right">Demanda (Consumo → Stock)</th>
                <th className="py-2.5 px-3 text-right">Stock Físico / Libre</th>
                <th className="py-2.5 px-3 text-right">🔒 A Reservar (MTO)</th>
                <th className="py-2.5 px-3 text-right">🛒 Déficit & Compra OAB</th>
                <th className="py-2.5 px-3 text-right">Costo Teórico / OAB</th>
                <th className="py-2.5 px-3 text-center">Estado Cobertura</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70 text-xs">
              {loading ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-slate-400">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-emerald-400" />
                    Calculando explosión MRP en 3 capas y conversión comercial UoM...
                  </td>
                </tr>
              ) : filteredMaterials.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-10 text-center text-slate-500">
                    No hay insumos que coincidan con el filtro seleccionado.
                  </td>
                </tr>
              ) : (
                filteredMaterials.map(line => {
                  const isChecked = Boolean(selectedKeys[line.key]);
                  const mtoVal = customMtoQty[line.key] ?? line.sugeridoReservarMTO;
                  const oabVal = customOabQty[line.key] ?? line.deficitComprarOAB;
                  const showBomConversion =
                    line.unidadConsumoBOM &&
                    line.unidadConsumoBOM !== line.unidad &&
                    line.demandaConsumoBOM !== line.cantidadTeoricaBruta;

                  return (
                    <tr
                      key={line.key}
                      className={`hover:bg-slate-800/50 transition ${
                        isChecked ? 'bg-slate-900/90' : 'opacity-60'
                      }`}
                    >
                      <td className="py-2.5 px-3">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={e =>
                            setSelectedKeys(prev => ({ ...prev, [line.key]: e.target.checked }))
                          }
                          className="rounded border-slate-700 bg-slate-950 text-emerald-500"
                        />
                      </td>
                      <td className="py-2.5 px-3 whitespace-nowrap">
                        <div className="flex flex-col gap-1 items-start">
                          {renderLayerBadge(line.capa, line.capaLabel)}
                          {line.esEstimacionParametrica && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-950/90 text-amber-300 border border-amber-700/60">
                              ⚠️ Est. Paramétrica
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="font-bold text-white flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-slate-950 text-emerald-300 border border-slate-800">
                            {line.sku}
                          </span>
                          <span>{line.nombre}</span>
                        </div>
                        {line.warningRatioReceta && (
                          <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-950/80 border border-amber-700/60 text-[10px] text-amber-300 font-semibold">
                            <AlertTriangle className="w-3 h-3 text-amber-400" />
                            <span>{line.warningRatioReceta}</span>
                          </div>
                        )}
                        <div className="text-[11px] text-slate-400 mt-0.5 flex flex-wrap gap-1">
                          <span className="text-slate-500">{line.categoria} · Origen:</span>
                          {line.origenMuebles.slice(0, 4).map((orig, i) => (
                            <span
                              key={i}
                              className="px-1.5 py-0.2 bg-slate-950 border border-slate-800 rounded text-[10px] text-slate-300 font-mono"
                            >
                              {orig}
                            </span>
                          ))}
                          {line.origenMuebles.length > 4 && (
                            <span className="text-[10px] text-slate-500">
                              +{line.origenMuebles.length - 4} más
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono">
                        <div className="font-bold text-white">
                          {line.cantidadTeoricaBruta} <span className="text-[10px] text-slate-400">{line.unidad}</span>
                        </div>
                        {showBomConversion && (
                          <div className="text-[10px] text-cyan-300">
                            Receta: {line.demandaConsumoBOM} {line.unidadConsumoBOM}
                          </div>
                        )}
                        {line.yaReservadoObra > 0 && (
                          <div className="text-[10px] text-purple-300">
                            Ya apartado: {line.yaReservadoObra}
                          </div>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono">
                        <div className="font-bold text-emerald-300">
                          Libre: {line.stockLibre} <span className="text-[10px] text-slate-400">{line.unidad}</span>
                        </div>
                        <div className="text-[10px] text-slate-400">
                          Físico: {line.stockFisico} | Otras: {line.apartadoOtrasObras}
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <input
                          type="number"
                          min={0}
                          max={line.stockLibre}
                          step="0.25"
                          value={mtoVal}
                          onChange={e =>
                            setCustomMtoQty(prev => ({
                              ...prev,
                              [line.key]: Math.max(
                                0,
                                Math.min(line.stockLibre, Number(e.target.value) || 0)
                              )
                            }))
                          }
                          className="w-20 bg-slate-950 border border-emerald-700/60 rounded px-2 py-1 text-right font-mono text-xs font-bold text-emerald-300 focus:outline-none"
                        />
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <div className="flex flex-col items-end gap-1">
                          <input
                            type="number"
                            min={0}
                            step="0.25"
                            value={oabVal}
                            onChange={e =>
                              setCustomOabQty(prev => ({
                                ...prev,
                                [line.key]: Math.max(0, Number(e.target.value) || 0)
                              }))
                            }
                            className="w-20 bg-slate-950 border border-amber-700/60 rounded px-2 py-1 text-right font-mono text-xs font-bold text-amber-300 focus:outline-none"
                          />
                          {(line.cantidadComercialCompraOAB ?? 0) > 0 && (
                            <div className="text-[10px] font-mono text-rose-300">
                              Compra: <strong>{line.cantidadComercialCompraOAB}</strong>{' '}
                              {line.uomCompra || line.unidad}
                              {(line.remanenteStockCompra ?? 0) > 0 && (
                                <span className="text-emerald-400 block">
                                  (+{line.remanenteStockCompra} {line.unidad} sobrante)
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono">
                        <div className="font-bold text-slate-200">
                          ${line.costoTotalRequeridoUSD.toFixed(2)}
                        </div>
                        <div className="text-[10px] text-slate-400">
                          @${line.costoUnitarioUSD.toFixed(2)}/{line.unidad}
                        </div>
                        {(line.costoCompraUSD ?? 0) > 0 &&
                          line.costoCompraUSD !== line.costoDeficitOABUSD && (
                            <div className="text-[10px] text-rose-300">
                              OAB: ${Number(line.costoCompraUSD).toFixed(2)}
                            </div>
                          )}
                      </td>
                      <td className="py-2.5 px-3 text-center whitespace-nowrap">
                        {renderCoverageBadge(line)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
